/**
 * The offline replay window — ONE source, because both halves of the system need it and
 * they must agree.
 *
 * MAINTAINER DECISION, 29 Jul 2026: thirty days. How long an unsent op may sit on a
 * phone before the app stops trusting it. It covers a holiday or a device left in a
 * drawer — a student captures photos on a Friday, does not open the app for three weeks,
 * and the work still lands.
 *
 * WHY IT LIVES IN `shared` RATHER THAN IN THE WEB APP THAT ENFORCES IT. Three places
 * derive from this number and they are on opposite sides of the wire:
 *
 *   * the browser queue gives up at `MAX_QUEUED_OP_AGE_MS` (platform/pending-ops.ts);
 *   * the server's receipt retention MUST EXCEED it, or a late replay finds its receipt
 *     already swept and performs its action a second time (jobs/receipts.ts);
 *   * the server's plausibility bound on a client-supplied movement instant uses it —
 *     an op older than the window could not have come from this queue.
 *
 * Increment 2 shipped the first two with the number written twice and the relationship
 * recorded only in a comment. That is the shape of drift: change one, and the guarantee
 * quietly inverts with every test still passing. Now there is one constant and the
 * inequality is asserted.
 */
export const MAX_QUEUED_OP_AGE_DAYS = 30;
export const MAX_QUEUED_OP_AGE_MS = MAX_QUEUED_OP_AGE_DAYS * 24 * 60 * 60 * 1000;
/**
 * Margin between the queue giving up and receipts being swept. Covers the gap between an
 * op being created and first applied, plus skew between a phone's clock and the
 * database's. Fifteen days is generous rather than calculated — the cost of being
 * generous here is a few extra small rows, and the cost of being tight is a double-apply.
 */
export const RECEIPT_RETENTION_MARGIN_DAYS = 15;
export const RECEIPT_RETENTION_DAYS = MAX_QUEUED_OP_AGE_DAYS + RECEIPT_RETENTION_MARGIN_DAYS;
/**
 * How far into the future a client's clock may be wrong before the server stops
 * believing its instant. Phones drift, and a few minutes of skew is ordinary; a movement
 * dated next week is not skew, it is a broken clock.
 */
export const CLIENT_CLOCK_SKEW_TOLERANCE_MS = 5 * 60 * 1000;
/**
 * Is a client-supplied instant one this system could plausibly have produced?
 *
 * NOT A SECURITY CHECK — a client that wants to lie about a minute can. It is a
 * DATA-QUALITY check, and its purpose is to keep an obviously broken device clock from
 * writing 1970 or 2099 into a shared history that people read to settle disputes. When
 * it says no, the caller records the SERVER's instant and marks the source as 'server',
 * so nothing is invented and nothing is lost: `recorded_at` was always going to be the
 * server's truth anyway (migration 017).
 */
export function isPlausibleClientInstant(instant, now = new Date()) {
    const offset = instant.getTime() - now.getTime();
    if (Number.isNaN(offset))
        return false;
    if (offset > CLIENT_CLOCK_SKEW_TOLERANCE_MS)
        return false;
    // Older than the window means it cannot have come from this queue, which gives up
    // first. The bound is the window itself plus the same skew tolerance, so an op sent at
    // the very edge of thirty days is not refused for being a few seconds late.
    return -offset <= MAX_QUEUED_OP_AGE_MS + CLIENT_CLOCK_SKEW_TOLERANCE_MS;
}
//# sourceMappingURL=queue-window.js.map